import Headroom from "react-headroom";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
} from "lucide-react";
import { DoctorCard } from "../components/booking/doctor/DoctorCard";
import { useLanguage } from "../i18n/LanguageContext";
import { DoctorCardSkeleton } from "../components/booking/doctor/CardSkeleton";
import { scrollToTop } from "../utils/scrollToTop";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMobile } from "../hooks/use-mobile";
import { DoctorDto } from "../web-api-client";
import ErrorRetry from "../components/ui/ErrorRetry";
import { format, isValid } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { useDebounce } from "../hooks/useDebounce";
import { formatDDMMYYYY, getSaturdayOfThisWeek } from "../utils/dateOptions";
import { useSessionStorageState } from "../hooks/useSessionStorageState";
import { useDoctors } from "../hooks/useDoctors";
import { useStickyLoading } from "../hooks/useStickyLoading";
import { useBookingTimeGuard } from "../hooks/useBookingTimeGuard";
import BookingTimeoutDialog from "../components/booking/BookingTimeoutDialog";

type ActiveTab = "all" | "today" | "tomorrow" | "weekend" | "available" | null;

export const DoctorList = () => {
  const PAGE_SIZE = 20;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const navigate = useNavigate();
  const [isMobile] = useIsMobile();
  const { t } = useLanguage();

  const { checkBookingTime, dialogOpen, dialogType, closeDialog } =
    useBookingTimeGuard();

  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [date, setDate] = useState<string>(""); // DD/MM/YYYY
  const [availableOnly, setAvailableOnly] = useState(true);
  const [d, setD] = useState<PickerValue | null>(null);
  const [open, setOpen] = useState(false);
  const [specialty] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>("all");
  const [pageNumber, setPageNumber] = useState(urlPage);
  const [mobileDoctors, setMobileDoctors] = useState<DoctorDto[]>([]);
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);

  const didMountRef = useRef(false);
  const [isResettingFilters, setIsResettingFilters] = useState(false);

  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpValue, setJumpValue] = useState<string>("");
  const jumpInputRef = useRef<HTMLInputElement | null>(null);

  const dDate = useDebounce(date, 250);
  const dSearch = useDebounce(searchTerm, 250);

  const {
    doctors,
    isFetching,
    isError,
    avatars,
    avatarErrors,
    totalCount,
    hasNextPage,
    refetch,
  } = useDoctors({
    date: dDate,
    specialty,
    availableOnly: true,
    searchTerm: dSearch,
    onlyConfigRoom: true,
    pageNumber,
    pageSize: PAGE_SIZE,
  });

  const [selectedDoctor, setSelectedDoctor] = useSessionStorageState(
    "selectedDoctor",
    null,
  );

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    setIsResettingFilters(true);
    setPageNumber(1);
    setIsFetchingNextPage(false);
    setMobileDoctors([]);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [dDate, dSearch, specialty, setSearchParams]);

  useEffect(() => {
    if (isMobile === undefined) return;

    if (!isMobile) {
      setMobileDoctors([]);
      return;
    }

    if (pageNumber === 1) {
      setMobileDoctors(doctors);
      setIsFetchingNextPage(false);
      setIsResettingFilters(false);
      return;
    }

    setMobileDoctors((prev) => {
      const existing = new Set(prev.map((d) => String(d.doctorId ?? "")));
      const next = doctors.filter(
        (d) => !existing.has(String(d.doctorId ?? "")),
      );
      return [...prev, ...next];
    });

    setIsFetchingNextPage(false);
  }, [doctors, isMobile, pageNumber]);

  useEffect(() => {
    if (!isMobile) return;
    if (isFetchingNextPage) setIsFetchingNextPage(false);
  }, [doctors, isMobile]);

  const items = useMemo(() => {
    if (!isMobile) return doctors;
    return mobileDoctors.length > 0 ? mobileDoctors : doctors;
  }, [isMobile, mobileDoctors, doctors]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadingRef = useRef(false);

  useEffect(() => {
    if (!isMobile) return;
    if (!sentinelEl) return;
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;
    if (isFetching) return;
    if (isResettingFilters) return;
    if (items.length === 0) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (isFetchingNextPage) return;
        if (isFetching) return;
        if (isResettingFilters) return;
        if (items.length === 0) return;

        setIsFetchingNextPage(true);
        setPageNumber((p) => p + 1);
      },
      { rootMargin: "120px" },
    );

    io.observe(sentinelEl);
    return () => io.disconnect();
  }, [
    isMobile,
    sentinelEl,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isResettingFilters,
    items.length,
  ]);

  useEffect(() => {
    if (!isFetching) {
      loadingRef.current = false;
      setIsFetchingNextPage(false);
    }
  }, [isFetching]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      window.scrollTo(0, 0);
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  useEffect(() => {
    document.title = t("doingubacsi");
  }, [t]);

  useEffect(() => {
    setPageNumber(urlPage);
  }, [urlPage]);

  useEffect(() => {
    // mobile: không sync page lên URL trong infinite scroll
    if (isMobile) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (pageNumber <= 1) next.delete("page");
        else next.set("page", String(pageNumber));
        return next;
      },
      { replace: true },
    );

    // desktop: đổi trang thì kéo lên đầu
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageNumber, setSearchParams, isMobile]);

  useEffect(() => {
    if (jumpOpen) {
      setJumpValue(String(pageNumber));
      // focus sau khi mở
      setTimeout(() => jumpInputRef.current?.focus(), 0);
    }
  }, [jumpOpen, pageNumber]);

  const commitJump = () => {
    const n = Math.floor(Number(jumpValue));
    if (!Number.isFinite(n)) {
      setJumpOpen(false);
      return;
    }
    const clamped = Math.min(totalPages, Math.max(1, n));
    setPageNumber(clamped);
    setJumpOpen(false);
  };

  const setAllDay = () => {
    setD(null);
    setDate("");
    setAvailableOnly(false);
    setActiveTab("all");
  };

  const setToday = () => {
    var date = new Date();
    setAvailableOnly(false);
    setDate(formatDDMMYYYY(date));
    setD(date);
    setActiveTab("today");
  };

  const setTomorrow = () => {
    var date = new Date();
    date.setDate(date.getDate() + 1);
    setAvailableOnly(false);
    setDate(formatDDMMYYYY(date));
    setD(date);
    setActiveTab("tomorrow");
  };

  const setWeekend = () => {
    var date = getSaturdayOfThisWeek();
    date.setDate(date.getDate() - 1);
    setD(date);
    setDate(formatDDMMYYYY(date));
    setActiveTab("weekend");
    setAvailableOnly(false);
  };

  const onCalendarChange = (value: PickerValue) => {
    if (value instanceof Date) {
      setAvailableOnly(false);

      const formatted =
        value && isValid(value) ? format(value, "dd/MM/yyyy") : null;
      setDate(formatted ?? "");

      var today = formatDDMMYYYY(new Date());
      var weekend = formatDDMMYYYY(getSaturdayOfThisWeek());

      if (today === formatted) {
        setActiveTab("today");
      } else if (weekend === formatted) {
        setActiveTab("weekend");
      } else {
        setActiveTab(null);
      }
    }
  };

  const handleBookAppointment = (doctor: DoctorDto) => {
    let doctorId = String(doctor.doctorId);

    if (!doctorId) return;

    if (!checkBookingTime()) {
      return;
    }

    scrollToTop();

    sessionStorage.clear();
    setSelectedDoctor(doctor);

    const params = new URLSearchParams({
      type: "doctor",
      doctorId,
      step: "chooseTime",
    });

    setTimeout(() => {
      navigate(`/dat-hen?${params.toString()}`);
    }, 0);
  };

  const handleViewDetails = (doctor: DoctorDto) => {
    if (!doctor.doctorId) return;

    const params = new URLSearchParams({
      doctorId: doctor.doctorId,
    });

    navigate(`/profile?${params.toString()}`);
  };

  const overlayVisible = useStickyLoading(
    isFetching && (!isMobile ? true : pageNumber === 1),
    {
      showDelay: 100,
      minVisible: 300,
    },
  );

  useEffect(() => {
    if (isMobile === undefined) return;

    if (isMobile) {
      setPageNumber(1);
      setMobileDoctors([]);
      setIsFetchingNextPage(false);

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }
  }, [isMobile, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (isMobile === undefined) return;

  return (
    <div className="min-h-screen max-w-5xl w-full mx-auto">
      <Headroom
        className={` z-10 
          ${searchTerm ? "always-visible" : ""}
          ${isMobile ? "headroom-custom-mobile" : "headroom-custom-desktop"}`}
      >
        <div className="flex flex-col gap-3 w-full items-center justify-center p-4 bg-white shadow-lg">
          <div className="grid grid-cols-2 w-full gap-3 items-center justify-center">
            <div className="relative w-full max-w-3xl flex-1">
              <Search className="hidden md:block absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                placeholder={t("timtheoten")}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full max-w-3xl pl-4 md:pl-12 pr-4 py-[0.7rem] rounded-lg border border-slate-200 focus:border-green-200 focus:ring-1 focus:ring-green-500 no-outline transition-all duration-200"
              />
            </div>
            <div className="relative">
              <DatePicker
                label={t("chonngay")}
                value={d}
                onChange={(newValue: any) => setD(newValue)}
                reduceAnimations
                minDate={new Date()}
                open={open}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                onAccept={(acceptedValue: any) => {
                  onCalendarChange(acceptedValue);
                }}
                slotProps={{
                  popper: { disablePortal: true },
                  openPickerButton: {
                    color: "success",
                  },
                  field: {
                    readOnly: true,
                    onClick: () => {
                      setOpen(true);
                    },
                    onPointerDownCapture: (e: any) => {
                      e.preventDefault();
                    },
                    onMouseDown: (e: any) => {
                      e.preventDefault();
                    },
                    onFocus: (e: any) => {
                      e.target.blur();
                      setOpen(true);
                    },
                  },
                  textField: {
                    inputProps: {},
                    variant: "outlined",
                    size: "small",
                    sx: {
                      flexShrink: "0",
                      width: isMobile ? "100%" : "100%",
                      "& .MuiInputLabel-root": {
                        position: "absolute",
                        left: 14,
                        top: "50%",
                        transform: "translateY(-50%) scale(1)",
                        transition: "transform .2s ease, top .2s ease",
                        pointerEvents: "none",
                      },

                      "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                        top: -6,
                        transform: "translateY(0) scale(0.75)",
                      },
                      "& .MuiPickersOutlinedInput-root": {
                        borderRadius: 2,
                        padding: isMobile ? "0.2rem 15px" : "1px 15px",

                        "& fieldset": {
                          borderWidth: d ? 2 : 1,
                          borderColor: d ? "#22c55e" : "#d1d5db",
                        },
                        "&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline":
                          {
                            borderColor: "#16A34A",
                          },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
          <div className="flex w-full gap-3">
            <div className="custom-scroll flex overflow-x-auto gap-2 w-full">
              <div
                onClick={setAllDay}
                className={`flex items-center justify-center cursor-pointer shrink-0 py-2 px-4 text-gray-700 border border-gray-200 rounded-md
                  ${
                    activeTab === "all" && "bg-green-600 font-medium text-white"
                  }`}
              >
                {t("tatca")}
              </div>
              <div
                onClick={setToday}
                className={`cursor-pointer flex items-center justify-center shrink-0 py-2 px-4 text-gray-700 border border-gray-200 rounded-md
                  ${
                    activeTab === "today" &&
                    "bg-green-600 font-medium text-white"
                  }`}
              >
                {t("homnay")}
              </div>
              <div
                onClick={setTomorrow}
                className={`cursor-pointer flex items-center justify-center shrink-0 py-2 px-4 text-gray-700 border border-gray-200 rounded-md
                  ${
                    activeTab === "tomorrow" &&
                    "bg-green-600 font-medium text-white"
                  }`}
              >
                {t("ngaymai")}
              </div>
              <div
                onClick={setWeekend}
                className={`cursor-pointer flex items-center justify-center shrink-0 py-2 px-4 text-gray-700 border border-gray-200 rounded-md
                  ${
                    activeTab === "weekend" &&
                    "bg-green-600 font-medium text-white"
                  }`}
              >
                {t("cuoituan")}
              </div>
            </div>
          </div>
        </div>
      </Headroom>

      <div className="mx-auto px-4 py-8">
        {overlayVisible ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <DoctorCardSkeleton key={index} />
            ))}
          </div>
        ) : isError && !isFetching ? (
          <div className="p-4 flex justify-center pb-[15px]">
            <ErrorRetry onRetry={refetch} />
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-anchor-none">
              {items.map((doctor, index) => (
                <DoctorCard
                  key={doctor.doctorId}
                  doctor={doctor}
                  onBookAppointment={handleBookAppointment}
                  onViewDetails={handleViewDetails}
                  hasAvatar={avatars?.[String(doctor.doctorId ?? "")]}
                  avatarError={avatarErrors[String(doctor.doctorId)]}
                />
              ))}
              {isMobile && hasNextPage && items.length > 0 && (
                <div ref={setSentinelEl}>
                  <DoctorCardSkeleton />
                </div>
              )}
            </div>

            {!isMobile && totalPages > 1 && (
              <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4">
                {/* Right: controls */}
                <div className="flex items-center gap-2">
                  {/* First */}
                  <button
                    className={`h-9 px-3 rounded-lg border bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition
                        ${pageNumber <= 1 && "hidden"}`}
                    onClick={() => setPageNumber(1)}
                    aria-label={t("trangdau")}
                    title={t("trangdau")}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>

                  {/* Prev */}
                  <button
                    className={`flex items-center justify-center h-9 px-3 rounded-lg border bg-primary text-white disabled:opacity-50 disabled:hover:bg-white transition
                        ${pageNumber <= 1 && "hidden"}`}
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    aria-label={t("trangtruoc")}
                    title={t("trangtruoc")}
                  >
                    <ChevronLeft className="w-4 h-4" /> {t("truoc")}
                  </button>

                  <div className="h-9 text-blue-700 flex items-center cursor-pointer">
                    {!jumpOpen ? (
                      <div
                        onClick={() => setJumpOpen(true)}
                        className="h-9 px-3 transition flex items-center gap-1"
                        title={t("nhaptrangtoi")}
                      >
                        <span className="font-semibold text-blue-600">
                          {pageNumber}
                        </span>
                        <span className="text-blue-600">/</span>
                        <span className="font-medium">{totalPages}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-2">
                        <input
                          ref={(el) => {
                            jumpInputRef.current = el;
                            if (el) {
                              requestAnimationFrame(() => {
                                el.focus({ preventScroll: true });
                                el.select();
                              });
                            }
                          }}
                          value={jumpValue}
                          onFocus={(e) => e.currentTarget.select()}
                          onClick={(e) => e.currentTarget.select()}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^\d]/g, "");
                            setJumpValue(v);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitJump();
                            if (e.key === "Escape") setJumpOpen(false);
                          }}
                          onBlur={() => commitJump()}
                          className="h-7 w-16 rounded-md border border-slate-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          inputMode="numeric"
                          placeholder={`${pageNumber}`}
                        />
                        <span className="text-slate-400 text-sm">/</span>
                        <span className="text-sm font-medium">
                          {totalPages}
                        </span>

                        <button
                          type="button"
                          onClick={commitJump}
                          className="h-7 px-2 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-xs"
                          title={t("toi")}
                        >
                          <span>{t("toi")}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Next */}
                  <button
                    className={`flex items-center justify-center h-9 px-3 rounded-lg border bg-primary  text-white disabled:opacity-50 disabled:hover:bg-white transition
                        ${pageNumber >= totalPages && "hidden"}`}
                    onClick={() =>
                      setPageNumber((p) => Math.min(totalPages, p + 1))
                    }
                    aria-label={t("trangsau")}
                    title={t("trangsau")}
                  >
                    {t("tiep")} <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Last */}
                  <button
                    className={`h-9 px-3 rounded-lg border bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition
                        ${pageNumber >= totalPages && "hidden"}`}
                    onClick={() => setPageNumber(totalPages)}
                    aria-label={t("trangcuoi")}
                    title={t("trangcuoi")}
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {items.length === 0 && !isFetching && (
              <div className="text-center py-12">
                <div className="rounded-full flex items-center justify-center mx-auto mb-4">
                  <img className="h-32 md:h-48" src="/images/no-doctor.png" />
                </div>
                <span className="md:text-lg text-base font-semibold text-slate-800 mb-2">
                  {t("khongtimthaybacsi")}
                </span>
              </div>
            )}

            {isMobile && isFetchingNextPage && (
              <div className="mt-6">
                <DoctorCardSkeleton />
              </div>
            )}
          </div>
        )}
        <div className="h-[90px]" />
      </div>

      <BookingTimeoutDialog
        isOpen={dialogOpen}
        type={dialogType}
        onClose={closeDialog}
      />
    </div>
  );
};
